import type { Disposable } from "@/shared/contracts/lifecycle";
import { getMessages } from "@/i18n/runtime";
import { huge, hugeZero, type HugeNumberInput } from "@/shared/numbers";
import type { HoverIdleSnapshot } from "../types";
import { formatForContext } from "../resources/numberFormat";

export interface HovCoreHudViewOptions {
  readonly secondaryResource?: "hov" | "ohov-power";
  readonly showRecentRate?: boolean;
  readonly showHovGain?: boolean;
}

export class HovCoreHudView implements Disposable {
  readonly root: HTMLElement;
  private readonly hovCoreLine: HTMLDivElement;
  private readonly hovCoreRateLine: HTMLDivElement | null;
  private readonly secondaryResourceLine: HTMLDivElement;
  private readonly hovGainLine: HTMLDivElement | null;
  private readonly secondaryResource: "hov" | "ohov-power";

  constructor(options: HovCoreHudViewOptions = {}) {
    const messages = getMessages();
    this.secondaryResource = options.secondaryResource ?? "hov";
    this.root = document.createElement("section");
    this.root.className = "hover-idle-hud-card hover-idle-hov-core-hud";
    this.root.setAttribute(
      "aria-label",
      messages.resourceHud.hovCoreHudAriaLabel,
    );

    this.hovCoreLine = document.createElement("div");
    this.hovCoreLine.className = "hover-idle-hud-card__primary";
    this.hovCoreLine.dataset.resourceLine = "hov-core";
    this.hovCoreLine.setAttribute(
      "aria-label",
      messages.resourceHud.hovCoreAmountAriaLabel,
    );

    this.hovCoreRateLine =
      options.showRecentRate === false ? null : document.createElement("div");
    if (this.hovCoreRateLine !== null) {
      this.hovCoreRateLine.className = "hover-idle-hud-card__secondary";
      this.hovCoreRateLine.dataset.resourceLine = "hov-core-rate";
    }

    this.secondaryResourceLine = document.createElement("div");
    this.secondaryResourceLine.className = "hover-idle-hud-card__secondary";
    this.secondaryResourceLine.dataset.resourceLine = this.secondaryResource;
    this.secondaryResourceLine.setAttribute(
      "aria-label",
      this.secondaryResource === "hov"
        ? messages.resourceHud.hovAmountAriaLabel
        : messages.resourceHud.oHovPowerAmountAriaLabel,
    );

    this.hovGainLine =
      options.showHovGain === true ? document.createElement("div") : null;
    if (this.hovGainLine !== null) {
      this.hovGainLine.className = "hover-idle-hud-card__secondary";
      this.hovGainLine.dataset.resourceLine = "hov-gain";
    }

    this.root.append(this.hovCoreLine);
    if (this.hovCoreRateLine !== null) this.root.append(this.hovCoreRateLine);
    this.root.append(this.secondaryResourceLine);
    if (this.hovGainLine !== null) this.root.append(this.hovGainLine);
  }

  update(
    snapshot: HoverIdleSnapshot,
    recentHovCoreRatePerHour: HugeNumberInput = hugeZero(),
  ): void {
    const messages = getMessages();
    this.hovCoreLine.textContent = messages.resourceHud.hovCoreAmount(
      formatForContext(snapshot.oHov.hovCore, "main"),
    );
    if (this.hovCoreRateLine !== null) {
      this.hovCoreRateLine.textContent = messages.resourceHud.hovCoreRecentRate(
        formatForContext(huge(recentHovCoreRatePerHour), "compact"),
      );
    }
    this.secondaryResourceLine.textContent =
      this.secondaryResource === "hov"
        ? messages.resourceHud.hovAmount(formatForContext(snapshot.hov, "main"))
        : messages.resourceHud.oHovPowerAmount(
            formatForContext(snapshot.oHov.power, "main"),
          );
    if (this.hovGainLine !== null) {
      this.hovGainLine.textContent = messages.resourceHud.hovGain(
        formatForContext(snapshot.hovGainPerSecond, "compact"),
      );
    }
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
  }

  dispose(): void {
    this.root.remove();
  }
}
